import AddOutlined from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlined from "@mui/icons-material/DeleteOutlineOutlined";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import { createFilterOptions, Tooltip } from "@mui/material";
import { renderOptionCountries } from "components/shared/AddSalesModal";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import { getInitialPriceFormValue } from "components/TitleFormContainer/utils";
import type { CountryCode } from "countries";
import { getCountries } from "countries";
import { formatNumber, getCurrenciesOptions, getCurrencyData, removeVatFromPrice } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useCallback, useEffect, useMemo, type ChangeEvent } from "react";
import { Controller, useFieldArray, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { isPriceHistoryDeletable } from "store/priceHistory/priceHistory.selectors";
import { priceHistorySubmit } from "store/priceHistory/priceHistory.use-cases";
import type { Currencies } from "store/settingsStore";

import { currenciesList } from "../../../currenciesList";
import type { IPriceHistoryEntry, IPriceHistoryFormValues } from "../../../store/priceHistory";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { FlagTag } from "../../TitleFormContainer/PriceChannelsStep";
import type { TFormCreateTitle } from "../../TitleFormContainer/types";
import { findChainPredecessor, isLastDatedPriceInChain, toDayKey } from "./utils";

interface PriceHistoryGridRowProps {
    entry: IPriceHistoryEntry;
    channelId?: number;
    titleId?: number;
    /** Total number of grid columns — for the full-width per-country sub-rows. */
    columnCount: number;
    onDelete: () => void;
}

const cellInputBase =
    "border border-border-color rounded h-8 py-1 text-sm text-grey-900 w-full outline-none focus:border-info disabled:bg-backgroundZ1 disabled:text-gray-400 disabled:cursor-not-allowed";
const cellInput = `${cellInputBase} px-2`;
// Native date inputs have a large intrinsic width; tighter padding keeps them inside their w-36 column.
const dateCellInput = `${cellInputBase} px-1`;

/**
 * One editable price period, rendered as a table row so every period lines up
 * in the shared grid. Inline auto-save: leaving the row submits it through the
 * existing `priceHistorySubmit` thunk (VAT conversion, overlap / single-open-end
 * validation, draft mutation, form errors, shallRecompute) — no business logic
 * is duplicated here. Per-country prices are the entry's `childCurrencies`,
 * shown as indented sub-rows.
 */
const PriceHistoryGridRow = ({ entry, channelId, titleId, columnCount, onDelete }: PriceHistoryGridRowProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { getCompanySettings, countryFeatures, companyCountryCode, companyCurrency } = useCompanySettings();

    const usesMultipleCurrencies = getCompanySettings("uses_multiple_currencies");
    const activeAlternativeCurrencies = JSON.parse(getCompanySettings("active_alternative_currencies"));
    const defaultCurrency = getCurrencyData(companyCurrency);
    const usesPriceWithVat = getCompanySettings("uses_price_with_vat") && countryFeatures.showPriceTTC;
    const usesMarketSegmentation = getCompanySettings("uses_market_segmentation");

    const deletable = useAppSelector(isPriceHistoryDeletable(channelId, entry.id));

    const currenciesOptions = useMemo(
        () => [defaultCurrency, ...getCurrenciesOptions(activeAlternativeCurrencies)],
        [activeAlternativeCurrencies, defaultCurrency],
    );

    const parentFormContext = useFormContext<TFormCreateTitle>();

    const {
        control,
        handleSubmit,
        reset,
        setError,
        formState: { errors },
        watch,
    } = useForm<IPriceHistoryFormValues>({
        defaultValues: {
            channelId,
            price: "",
            currency: companyCurrency,
            startDate: null,
            endDate: null,
            childCurrencies: [],
        },
        mode: "onChange",
    });

    const price = watch("price");
    const currency = watch("currency");
    const startDateValue = watch("startDate");

    const {
        fields: childCurrencyFields,
        append: appendChildCurrency,
        remove: removeChildCurrency,
    } = useFieldArray({ control, name: "childCurrencies" });

    const allCountries = useMemo(
        () =>
            getCountries()
                .filter((c) => !c.silent)
                .sort((c1, c2) => c1.name.localeCompare(c2.name))
                .map((c) => ({ ...c, value: c.code })),
        [],
    );
    const getCountryValueFromCode = useCallback(
        (code: CountryCode) => allCountries.find((c) => c.code === code),
        [allCountries],
    );

    const getInitialPrice = useCallback(
        (p: number, curr: Currencies, countryCode?: CountryCode) =>
            getInitialPriceFormValue({
                companyCurrency,
                vatCountryCode: countryCode ?? companyCountryCode,
                currency: curr ?? companyCurrency,
                usesPriceWithVat,
                price: p,
            }),
        [companyCurrency, companyCountryCode, usesPriceWithVat],
    );

    // Converse figure for the secondary column: when the input is TTC (usesPriceWithVat),
    // show the HT value; only meaningful for the company currency (EUR).
    const conversePrice = useCallback(
        (p: string, curr: Currencies, countryCode?: string) => {
            if (curr !== "EUR") return "—";
            return formatNumber(
                removeVatFromPrice(Number(p), countryCode ?? companyCountryCode),
                { isCurrency: true, always2Digit: true, preferredCurrency: curr },
                { maximumFractionDigits: 2, minimumFractionDigits: 2 },
            );
        },
        [companyCountryCode],
    );

    useEffect(() => {
        reset({
            channelId: entry.channelId,
            price: getInitialPrice(entry.price, entry.currency),
            currency: entry.currency,
            startDate: entry.startDate ? new Date(`${entry.startDate.substring(0, 10)}T00:00:00`) : null,
            endDate: entry.endDate ? new Date(`${entry.endDate.substring(0, 10)}T00:00:00`) : null,
            childCurrencies:
                entry.childCurrencies?.map((c) => ({
                    currency: c.currency,
                    country: getCountryValueFromCode(c.country),
                    price: getInitialPrice(c.price, c.currency, c.country),
                })) ?? [],
        });
    }, [entry, getCountryValueFromCode, getInitialPrice, reset]);

    const onSubmit = async (data: IPriceHistoryFormValues) => {
        const normalized: IPriceHistoryFormValues = {
            ...data,
            price: (data.price ?? "").toString().replace(",", "."),
            childCurrencies: data.childCurrencies.map((c) => ({
                ...c,
                price: (c.price ?? "").toString().replace(",", "."),
            })),
        };
        await dispatch(
            priceHistorySubmit({
                companyCountryCode,
                data: normalized,
                isEditing: true,
                parentFormContext,
                setError,
                t,
                titleId,
                usesPriceWithVat,
                channelId,
                editEntry: entry,
            }),
        );
    };

    const save = handleSubmit(onSubmit);

    const handleDateChange = (onChange: (date: Date | null) => void) => (e: ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value ? new Date(`${e.target.value}T00:00:00`) : null);
        void save();
    };

    // Periods chain per (channelId, currency): a period that follows another one
    // always starts the day after it ends, so its start date is edited through
    // the previous period's end date, never directly.
    const startLocked = useAppSelector((state) =>
        Boolean(findChainPredecessor(state.priceHistory.draftEntries, entry)),
    );
    // The last dated price must stay open-ended so a price always covers the future.
    const endLocked = useAppSelector((state) => isLastDatedPriceInChain(state.priceHistory.draftEntries, entry));

    // A rejected save (e.g. overlap) can become valid when *another* row moves:
    // re-submit a row still in error whenever the other periods of the channel
    // change, so stale errors clear and the rejected values finally persist.
    const otherEntriesKey = useAppSelector((state) =>
        state.priceHistory.draftEntries
            .filter((e) => e.channelId === channelId && e.id !== entry.id)
            .map((e) => `${e.id}:${e.startDate}:${e.endDate}:${e.currency}`)
            .join("|"),
    );
    useEffect(() => {
        if (Object.keys(errors).length > 0) void save();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otherEntriesKey]);

    const currencySelect = (name: `currency` | `childCurrencies.${number}.currency`) => (
        <Controller
            name={name}
            control={control}
            render={({ field }) => (
                <div className="relative">
                    <FlagTag
                        className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none"
                        flagUrl={currenciesList().find(({ code }) => code == field.value)?.flag}
                    />
                    <select
                        {...field}
                        aria-label={t("PriceHistory.Currency")}
                        className={`${cellInputBase} appearance-none pl-8 pr-6 bg-white cursor-pointer`}
                    >
                        {currenciesOptions.map((c) => (
                            <option key={c?.code} value={c?.code}>
                                {c?.code}
                            </option>
                        ))}
                    </select>
                    <KeyboardArrowDownRounded
                        sx={{ fontSize: 16 }}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
                    />
                </div>
            )}
        />
    );

    return (
        <>
            <tr className="border-b border-border-color align-top" onBlur={save}>
                {/* Prix HT / TTC */}
                <td className="py-1.5 px-2">
                    <Controller
                        name="price"
                        control={control}
                        render={({ field }) => (
                            <input
                                type="text"
                                inputMode="decimal"
                                placeholder="0.00"
                                aria-label={usesPriceWithVat ? t("NewTitle.PriceVAT") : t("NewTitle.Price")}
                                className={`${cellInput} max-w-40`}
                                value={field.value ?? ""}
                                onChange={(e) => field.onChange(e.target.value)}
                            />
                        )}
                    />
                    {usesPriceWithVat && conversePrice(price, currency) !== "—" && (
                        <span className="block mt-0.5 text-xs text-gray-500 whitespace-nowrap tabular-nums">
                            {t("PriceHistory.PriceHT")} : {conversePrice(price, currency)}
                        </span>
                    )}
                    {errors.price && <span className="text-xs text-red-500">{errors.price.message}</span>}
                </td>

                {/* Devise */}
                {usesMultipleCurrencies && <td className="py-1.5 px-2 w-28">{currencySelect("currency")}</td>}

                {/* Dates — a price with no dates at all covers all time and shows dashes;
                    dates become editable by adding a price on new dates */}
                {entry.startDate === null && entry.endDate === null ? (
                    <>
                        <td className="py-1.5 px-2 h-11 text-sm text-gray-500">—</td>
                        <td className="py-1.5 px-2 h-11 text-sm text-gray-500">—</td>
                    </>
                ) : (
                    <>
                        <td className="py-1.5 px-2">
                            <Controller
                                name="startDate"
                                control={control}
                                render={({ field }) => (
                                    <Tooltip title={startLocked ? t("PriceHistory.StartDateLocked") : ""} arrow>
                                        {/* span wrapper: a disabled input emits no pointer events for the Tooltip */}
                                        <span className="block">
                                            <input
                                                type="date"
                                                aria-label={t("PriceHistory.StartDate")}
                                                className={dateCellInput}
                                                disabled={startLocked}
                                                value={field.value ? toDayKey(field.value) : ""}
                                                onChange={handleDateChange(field.onChange)}
                                            />
                                        </span>
                                    </Tooltip>
                                )}
                            />
                        </td>
                        <td className="py-1.5 px-2">
                            <Controller
                                name="endDate"
                                control={control}
                                render={({ field }) => (
                                    <Tooltip title={endLocked ? t("PriceHistory.EndDateLocked") : ""} arrow>
                                        {/* span wrapper: a disabled input emits no pointer events for the Tooltip */}
                                        <span className="block">
                                            <input
                                                type="date"
                                                aria-label={t("PriceHistory.EndDate")}
                                                className={dateCellInput}
                                                disabled={endLocked}
                                                min={startDateValue ? toDayKey(startDateValue) : undefined}
                                                value={endLocked || !field.value ? "" : toDayKey(field.value)}
                                                onChange={handleDateChange(field.onChange)}
                                            />
                                        </span>
                                    </Tooltip>
                                )}
                            />
                            {errors.endDate && <span className="text-xs text-red-500">{errors.endDate.message}</span>}
                        </td>
                    </>
                )}

                {/* Action */}
                <td className="py-1.5 px-2 text-right">
                    {deletable && (
                        <button
                            type="button"
                            onClick={onDelete}
                            title={t("PriceHistory.DeleteEntry")}
                            className="inline-flex h-8 items-center bg-transparent border-0 cursor-pointer text-gray-400 hover:text-dark-red transition-colors p-0"
                        >
                            <DeleteOutlineOutlined sx={{ fontSize: 18 }} />
                        </button>
                    )}
                </td>
            </tr>

            {/* Prix par pays (childCurrencies) */}
            {usesMarketSegmentation &&
                childCurrencyFields.map((field, i) => {
                    const childCurrency = watch(`childCurrencies.${i}.currency`);
                    return (
                        <tr key={field.id} className="bg-backgroundZ1 border-b border-border-color" onBlur={save}>
                            <td colSpan={columnCount} className="py-1.5 pl-8 pr-3">
                                <div className="flex items-end gap-2">
                                    <AutocompleteFormComponent
                                        value={field.country}
                                        className="min-w-64 flex-1"
                                        label=""
                                        control={control}
                                        controlName={`childCurrencies.${i}.country`}
                                        options={allCountries}
                                        renderOptionText={(option) => option.name ?? ""}
                                        isOptionEqualToValue={(option, value) => option?.code === value?.code}
                                        renderOption={renderOptionCountries}
                                        filterOptions={createFilterOptions({
                                            matchFrom: "any",
                                            stringify: (option: any) => option.name + option.code,
                                        })}
                                    />
                                    {usesMultipleCurrencies && (
                                        <div className="w-28">{currencySelect(`childCurrencies.${i}.currency`)}</div>
                                    )}
                                    <div className="w-32">
                                        <Controller
                                            name={`childCurrencies.${i}.price`}
                                            control={control}
                                            render={({ field: priceField }) => (
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    placeholder="0.00"
                                                    aria-label={
                                                        usesPriceWithVat && childCurrency === "EUR"
                                                            ? t("NewTitle.PriceVAT")
                                                            : t("NewTitle.Price")
                                                    }
                                                    className={cellInput}
                                                    value={priceField.value ?? ""}
                                                    onChange={(e) => priceField.onChange(e.target.value)}
                                                />
                                            )}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeChildCurrency(i)}
                                        title={t("PriceHistory.DeleteEntry")}
                                        className="bg-transparent border-0 cursor-pointer text-gray-400 hover:text-dark-red transition-colors p-1 mb-0.5"
                                    >
                                        <DeleteOutlineOutlined sx={{ fontSize: 18 }} />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    );
                })}

            {usesMarketSegmentation && (
                <tr className="border-b border-border-color">
                    <td colSpan={columnCount} className="pl-8 pr-3 py-1.5">
                        <button
                            type="button"
                            onClick={() =>
                                appendChildCurrency({
                                    currency: (currenciesOptions[0]?.code ?? "EUR") as Currencies,
                                    country: getCountryValueFromCode("FR")!,
                                    price,
                                })
                            }
                            className="flex items-center gap-1 w-fit p-0 text-xs font-medium text-info bg-transparent border-0 cursor-pointer hover:underline"
                        >
                            <AddOutlined sx={{ fontSize: 14 }} /> {t("PriceHistory.AddChildCurrency")}
                        </button>
                    </td>
                </tr>
            )}
        </>
    );
};

export default PriceHistoryGridRow;
